import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getMenuItem, type MenuItem } from '@/lib/menu';

export interface CartLine {
  item: MenuItem;
  qty: number;
}

interface CartContextValue {
  lines: CartLine[];
  /** Total number of items (sum of quantities) */
  count: number;
  subtotal: number;
  deliveryFee: number;
  total: number;
  isCartOpen: boolean;
  setCartOpen: (open: boolean) => void;
  addItem: (item: MenuItem, qty?: number) => void;
  removeItem: (id: string) => void;
  setQty: (id: string, qty: number) => void;
  getQty: (id: string) => number;
  clear: () => void;
}

export const DELIVERY_FEE = 100;
export const FREE_DELIVERY_FROM = 1500;

const STORAGE_KEY = 'mlo:cart';

const CartContext = createContext<CartContextValue | undefined>(undefined);

function readStoredCart(): Array<[string, number]> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is [string, number] =>
        Array.isArray(entry) && typeof entry[0] === 'string' && typeof entry[1] === 'number' && entry[1] > 0,
    );
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [quantities, setQuantities] = useState<Array<[string, number]>>(readStoredCart);
  const [isCartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(quantities));
    } catch {
      // storage unavailable — cart still works in-memory
    }
  }, [quantities]);

  const addItem = useCallback((item: MenuItem, qty = 1) => {
    setQuantities((prev) => {
      const existing = prev.find(([id]) => id === item.id);
      if (existing) {
        return prev.map(([id, q]) => (id === item.id ? [id, Math.min(q + qty, 99)] : [id, q]));
      }
      return [...prev, [item.id, qty]];
    });
  }, []);

  const removeItem = useCallback((id: string) => {
    setQuantities((prev) => prev.filter(([lineId]) => lineId !== id));
  }, []);

  const setQty = useCallback((id: string, qty: number) => {
    setQuantities((prev) =>
      qty <= 0
        ? prev.filter(([lineId]) => lineId !== id)
        : prev.map(([lineId, q]) => (lineId === id ? [lineId, Math.min(qty, 99)] : [lineId, q])),
    );
  }, []);

  const getQty = useCallback(
    (id: string) => quantities.find(([lineId]) => lineId === id)?.[1] ?? 0,
    [quantities],
  );

  const clear = useCallback(() => setQuantities([]), []);

  const { lines, count, subtotal } = useMemo(() => {
    const resolved: CartLine[] = [];
    let itemCount = 0;
    let sum = 0;
    for (const [id, qty] of quantities) {
      const item = getMenuItem(id);
      if (!item) continue;
      resolved.push({ item, qty });
      itemCount += qty;
      sum += item.price * qty;
    }
    return { lines: resolved, count: itemCount, subtotal: sum };
  }, [quantities]);

  const deliveryFee = subtotal === 0 || subtotal >= FREE_DELIVERY_FROM ? 0 : DELIVERY_FEE;
  const total = subtotal + deliveryFee;

  const value: CartContextValue = {
    lines,
    count,
    subtotal,
    deliveryFee,
    total,
    isCartOpen,
    setCartOpen,
    addItem,
    removeItem,
    setQty,
    getQty,
    clear,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
